import { inject, Injectable } from '@angular/core';
import toPath from 'lodash-es/toPath';
import { map, Observable } from 'rxjs';

import { PageContextFacade } from '@blaze-runtime/engine-data-access';
import { CanvasElementId } from '@blaze-runtime/engine-domain';
import { ComparisonOperator } from '@blaze-shared/api-contracts-domain';
import { DatasetsApiService } from '@blaze-shared/datasets-data-access';
import { getAllComparisonNodes } from '@blaze-shared/datasets-domain';
import {
  CanvasElementDataProperties,
  DataBindingSourceInfo,
  getActiveBinding,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService, RUNTIME_CONTEXT_KEYS } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  BlazeSchema,
  convertLegacySchemaToWorkflowSchema,
  createElementSchemaId,
  DatasetColumn,
  DatasetRow,
  DatasetSchema,
  getColumnQuery,
  isTagId,
  isWorkflowSchema,
  matchColumn,
  prepareInitialValue,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  DatasetWorkflowNodeBase,
  getWorkflowNodeById,
  Workflow,
  WorkflowNode,
  WorkflowNodeWithCondition,
  WorkflowNodeWithQuery,
  WorkflowNodeWithQueryConditions,
} from '@blaze-shared/workflow-domain';

import { RuntimeFacade } from '../+state/runtime/runtime.facade';

export interface SourcePrimaryTableInfo {
  node?: WorkflowNode | null;
  columns: DatasetColumn[];
  datasetInfo?: DataBindingSourceInfo | undefined;
}

@Injectable({ providedIn: 'root' })
export class RowGenerationService {
  private readonly datasetsApiService = inject(DatasetsApiService);
  private readonly iss = inject(IntelliSenseService);
  private readonly runtimeFacade = inject(RuntimeFacade);
  private readonly pageContextFacade = inject(PageContextFacade);
  private readonly schemaService = inject(SchemaService);

  newDataRowCache: Record<CanvasElementId, DatasetRow> = {};

  generateRowId(): Observable<string> {
    return this.datasetsApiService.getGenerateId().pipe(map(({ id }) => id));
  }

  getCachedNewRow(
    elementId?: CanvasElementId,
    data?: CanvasElementDataProperties[],
    bustCache = false,
  ): DatasetRow {
    if (!elementId) {
      return {};
    }

    let newRowObj = this.newDataRowCache[elementId];
    if (!newRowObj || bustCache) {
      const dataSource = getActiveBinding(data, 'optionsList')?.dataSource;
      this.newDataRowCache[elementId] = newRowObj = this.getNewRow(dataSource, {
        prefillData: false,
        useFullContextSchema: true,
      });
    }
    return { ...newRowObj };
  }

  getNewRow(
    dataSource: DataBindingSourceInfo | undefined,
    args?: {
      customFields?: string[];
      initValue?: any;
      nullIdColumn?: boolean;
      prefillData?: boolean;
      useFullContextSchema?: boolean;
    },
  ): DatasetRow {
    if (!dataSource) {
      return {};
    }

    const { prefillData = true, nullIdColumn = false, initValue, customFields } = args ?? {};
    switch (dataSource.sourceType) {
      case 'formGroup': // fallthrough
      case 'dataset': {
        const result = this.getEmptyRow(dataSource.columns, nullIdColumn, initValue, customFields);
        return result;
      }
      case 'context': {
        const { columns, node } = this.getColumnsAndNode(dataSource, !!args?.useFullContextSchema);
        const result = this.getEmptyRow(columns, nullIdColumn, initValue, customFields);
        if (prefillData) {
          if (node && 'query' in node && !!node.query) {
            this.tryFillRowValuesQuery(result, dataSource, columns, node as WorkflowNodeWithQuery);
          } else if (node && 'queryConditions' in node && !!node.queryConditions) {
            this.tryFillRowValues(
              result,
              dataSource,
              columns,
              node as WorkflowNodeWithQueryConditions,
            );
          }
        }
        return result;
      }
      case 'column-tags':
        return { value: undefined };
      case 'column-filter': {
        const result = { value: null };
        return result;
      }
      case 'column-filter-ranged': {
        const result = { value: null, label: '' };
        return result;
      }
      default:
        break;
    }
    return {};
  }

  private getColumnsAndNode(
    dataSource: DataBindingSourceInfo,
    useFullContextSchema: boolean,
  ): SourcePrimaryTableInfo {
    if (dataSource.sourceType !== 'context') {
      throw new Error('invalid source type');
    }

    const inputSchema = this.runtimeFacade.workflowsSchemaValue[dataSource.id];
    const workflow = this.runtimeFacade.workflowsValue.find((wf) => wf.id === dataSource.id);

    if (workflow && !useFullContextSchema) {
      const { node, schema } = this.getPrimarySourceSchema(workflow, inputSchema);
      return { node, columns: schema.columns || [] };
    }

    if (inputSchema?.columns) {
      return { columns: inputSchema.columns };
    }

    return { columns: dataSource.columns };
  }

  private getEmptyRow(
    columns: DatasetColumn[],
    nullIdColumn = false,
    initValue?: any,
    customFields?: string[],
  ): DatasetRow {
    const result = columns.reduce((result, column) => {
      const columnName = this.schemaService.getColumnNameLatest(column);
      result[columnName] = initValue?.[columnName] ?? prepareInitialValue(column, nullIdColumn);
      return result;
    }, {} as any);

    if (customFields?.length) {
      for (const field of customFields) {
        result[field] = initValue?.[field] ?? null;
      }
    }
    return result;
  }

  private tryFillRowValues(
    result: DatasetRow,
    dataSource: DataBindingSourceInfo,
    targetColumns: DatasetColumn[],
    node?: WorkflowNodeWithQueryConditions & WorkflowNodeWithCondition,
  ): void {
    // NOTE the WF condition must be able to values items 1:1 (ie. can't be >, < etc.)
    if (!node || (!node.queryConditions?.length && !node.condition?.includes('==='))) {
      return;
    }

    const inputWf = this.runtimeFacade.workflowsValue.find(({ id }) => id === dataSource.id);
    if (!inputWf) {
      return;
    }

    const inputsCache: Record<string, any> = {};
    const inputSources = this.getInputSources(inputWf, inputsCache);

    const exprTokens = node.condition ? this.iss.tokenizeExpression(node.condition) : undefined;

    targetColumns.forEach((column) => {
      let fieldIdOrName: any;
      let fieldValue: any;

      const columnName = this.schemaService.getColumnNameLatest(column);

      if (node.queryConditions?.length) {
        const linkQuery = node.queryConditions.find(
          (q) =>
            q.columns.some((idOrName) => matchColumn(getColumnQuery(idOrName), column)) && // Find condition which's column id or name matches the column
            q.operator === ComparisonOperator.Eq,
        );
        if (!linkQuery?.value) {
          return;
        }
        fieldValue = linkQuery.value;
        fieldIdOrName = toPath(fieldValue)[1]; // expected pattern: $$input.fieldIdOrName / $$input['fieldIdOrName']
      } else if (exprTokens?.length) {
        const linkExpr = exprTokens.find(
          (et) => (column.id && et.includes(column.id)) || et.includes(column.name),
        );
        if (!linkExpr) {
          return;
        }
        const linkedField = linkExpr.find((e) => e.startsWith('$$'));
        if (!linkedField) {
          return;
        }
        fieldValue = linkedField;
        fieldIdOrName = linkExpr.find((e) => e !== '===' && !e.startsWith('$$'));
      }

      fieldValue = this.getFieldValue(
        column,
        fieldValue,
        result,
        columnName,
        inputSources,
        fieldIdOrName,
      );
    });
  }

  private tryFillRowValuesQuery(
    result: DatasetRow,
    dataSource: DataBindingSourceInfo,
    targetColumns: DatasetColumn[],
    node: WorkflowNodeWithQuery & WorkflowNodeWithCondition,
  ): void {
    // NOTE the WF condition must be able to values items 1:1 (ie. can't be >, < etc.)
    if (!node.query) {
      return;
    }

    const comparisonNodes = getAllComparisonNodes(node.query);
    if (!comparisonNodes.length && !node.condition?.includes('===')) {
      return;
    }

    const inputWf = this.runtimeFacade.workflowsValue.find(({ id }) => id === dataSource.id);
    if (!inputWf) {
      return;
    }

    const inputsCache: Record<string, any> = {};
    const inputSources = this.getInputSources(inputWf, inputsCache);

    targetColumns.forEach((column) => {
      let fieldIdOrName: any;
      let fieldValue: any;

      const columnName = this.schemaService.getColumnNameLatest(column);

      if (comparisonNodes.length) {
        const linkQuery = comparisonNodes.find(
          (q) =>
            matchColumn(getColumnQuery(q.column), column) && q.operator === ComparisonOperator.Eq,
        );
        if (!linkQuery?.value) {
          return;
        }
        fieldValue = linkQuery.value;
        fieldIdOrName = toPath(fieldValue)[1]; // expected pattern: $$input.fieldIdOrName / $$input['fieldIdOrName']
      }

      fieldValue = this.getFieldValue(
        column,
        fieldValue,
        result,
        columnName,
        inputSources,
        fieldIdOrName,
      );
    });
  }

  private getPrimarySourceSchema(
    workflow: Workflow,
    schema?: BlazeSchema | WorkflowSchema | null,
  ): { node: WorkflowNode; schema: DatasetSchema | WorkflowSchema } {
    if (!schema) {
      schema = {
        columns: [],
        displayColumn: '',
        id: '',
        name: '',
      };
    }
    if (!isWorkflowSchema(schema)) {
      schema = convertLegacySchemaToWorkflowSchema(schema, workflow.id);
    }
    let node = getWorkflowNodeById(workflow?.startNodeId, workflow) as WorkflowNode;

    while (node) {
      switch (node.nodeType) {
        case 'ApiCall':
          // Currently we have no way of checking schema of the API call. As such, we only return schema which is
          // known to us.
          return { node, schema };
        case 'Get':
        case 'Filter': {
          const datasetId = (node as DatasetWorkflowNodeBase).datasetId;
          if (datasetId) {
            return {
              node,
              schema: this.iss.getSchemaForDataset(schema as WorkflowSchema, node),
            };
          }
          break;
        }
        default:
          break;
      }
      node = getWorkflowNodeById(node?.nextNodeId, workflow) as WorkflowNode;
    }
    return { node, schema };
  }

  private getInputSources(
    inputWf: Workflow,
    inputsCache: Record<string, any>,
  ): Record<string, any> {
    const inputsData = Object.entries(inputWf.inputs).reduce<Record<string, any>>(
      (acc, [input, connectionId]) => {
        const connection = this.runtimeFacade.connectionsValue.find(
          ({ id }) => id === connectionId,
        );
        if (connection) {
          const srcKey = createElementSchemaId(connection.source.id, connection.eventType);
          const srcOutput =
            inputsCache[srcKey] ||
            (inputsCache[srcKey] = this.runtimeFacade.outputsUiValue[srcKey]?.data);
          if (srcOutput !== undefined) {
            acc[input] = srcOutput;
          }
        }
        return acc;
      },
      {},
    );
    const ns = inputWf.pageId ?? this.runtimeFacade.pageId;
    const ctx = this.iss.createRuntimeContext({
      inputsData,
      pageContext: ns ? this.pageContextFacade.getPageContextForPageSync(ns) : undefined,
      urlVars: this.runtimeFacade.getVarUrl(ns),
    });
    return ctx;
  }

  private getFieldValue(
    column: DatasetColumn,
    fieldValue: string,
    result: DatasetRow,
    columnName: string,
    inputSources: Record<string, any>,
    fieldIdOrName: string,
  ): string {
    // Check if matching tag found
    const isMatchingTag =
      column.columnType === 'Tag' &&
      column.tagDefinition?.items.find((tag) =>
        isTagId(fieldValue) ? tag.id === fieldValue : tag.value === fieldValue,
      );
    // Resolve any tag ids to values
    fieldValue = this.schemaService.replaceTagIdsWithValues(fieldValue);

    if (fieldValue && isMatchingTag) {
      result[columnName] = fieldValue;
    } else if (fieldValue) {
      const fieldRawName = fieldValue.substring(0, getFirstSeparator(fieldValue));
      const field = fieldRawName.substring(RUNTIME_CONTEXT_KEYS.includes(fieldRawName) ? 0 : 2);
      if (field in inputSources) {
        const colName = this.schemaService.getColumnName(fieldIdOrName) ?? '';
        if (colName) {
          result[columnName] = inputSources[field][colName];
        } else {
          result[columnName] = inputSources[field];
        }
      } else {
        result[columnName] = fieldValue;
      }
    }
    return fieldValue;
  }
}

function getFirstSeparator(field: string): number | undefined {
  const [idxDot, idxBracket] = [field.indexOf('.'), field.indexOf('[')];
  return idxDot >= 0
    ? idxBracket >= 0
      ? Math.min(idxDot, idxBracket)
      : idxDot
    : idxBracket >= 0
      ? idxBracket
      : undefined;
}
